// src/components/Analytics/ExpenseAnalytics.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../../firebase/config";
import { collection, getDoc, getDocs, doc } from "firebase/firestore";
import { DateTime } from "luxon";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import "../../styles/Analytics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

// ─────────────────────────── Date range helpers
const formatMDY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

function buildRangeFromPreset(preset, tz) {
  const end = DateTime.now()
    .setZone(tz)
    .startOf("day");
  let start = end;

  switch (preset) {
    case "LAST_7":
      start = end.minus({ days: 6 });
      break;
    case "LAST_30":
      start = end.minus({ days: 29 });
      break;
    case "LAST_90":
      start = end.minus({ days: 89 });
      break;
    case "MTD":
      start = end.startOf("month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    default:
      start = end.minus({ days: 29 });
  }

  return { startISO: start.toISODate(), endISO: end.toISODate() };
}

function getExpenseISODateInTZ(expense, tz) {
  // Prefer explicit expenseDate fields
  const v = expense?.expenseDate ?? expense?.expenseDateTs ?? expense?.date;

  // ISO string
  if (typeof v === "string") {
    const dt = DateTime.fromISO(v, { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  // Firestore Timestamp
  if (v && typeof v?.toDate === "function") {
    const dt = DateTime.fromJSDate(v.toDate(), { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  // millis
  if (typeof v === "number") {
    const dt = DateTime.fromMillis(v, { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  // fallback: createdAt if present
  const c = expense?.createdAt;
  if (c && typeof c?.toDate === "function") {
    const dt = DateTime.fromJSDate(c.toDate(), { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  return null;
}

/**
 * ✅ UPDATED:
 * - Adds Date Range (preset + custom) + MM/DD/YYYY range pill
 * - Filters scoped expenses by range (TZ-safe)
 * - Monthly chart reflects selected range (capped to last 12 months if range is larger)
 */
export default function ExpenseAnalytics({ companyId, employeeId = null }) {
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState({}); // for department lookup (company-wide only)

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // Budget state (same shape as EmployerExpense)
  const [budget, setBudget] = useState({
    monthlyBudget: 0,
    yearlyBudget: 0,
    remainingMonthly: 0,
    remainingYearly: 0,
    monthKey: "",
    yearKey: "",
  });

  const isDrilldown = !!employeeId;

  // ─────────────────────────── helpers
  const money = useCallback(
    (n) =>
      Number(n || 0).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }),
    []
  );

  const toMonthKey = useCallback(
    (isoOrTs) => {
      try {
        const dt =
          typeof isoOrTs === "string"
            ? DateTime.fromISO(isoOrTs, { zone: companyTimeZone })
            : isoOrTs?.toDate
            ? DateTime.fromJSDate(isoOrTs.toDate(), { zone: companyTimeZone })
            : DateTime.fromJSDate(new Date(isoOrTs), { zone: companyTimeZone });
        return dt.isValid ? dt.toFormat("yyyy-LL") : "Unknown";
      } catch {
        return "Unknown";
      }
    },
    [companyTimeZone]
  );

  // ─────────────────────────── guard (no early return)
  useEffect(() => {
    if (!companyId) {
      setError("Company not loaded.");
      setLoading(false);
    } else {
      setError("");
    }
  }, [companyId]);

  // ─────────────────────────── company timezone
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const c = await getDoc(doc(db, "companies", companyId));
        setCompanyTimeZone(c.data()?.timeZone || "UTC");
      } catch {
        setCompanyTimeZone("UTC");
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyTimeZone) return;

    if (rangePreset === "CUSTOM") {
      const todayISO = DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();

      // only fill blanks, don’t overwrite user input
      if (!rangeStart) setRangeStart(todayISO);
      if (!rangeEnd) setRangeEnd(todayISO);
      return;
    }

    const { startISO, endISO } = buildRangeFromPreset(
      rangePreset,
      companyTimeZone
    );

    // only update if different (prevents re-render loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, companyTimeZone, rangeStart, rangeEnd]); // ✅ don’t include rangeStart/rangeEnd here

  // ✅ Guard: keep start <= end
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = DateTime.fromISO(rangeStart, { zone: companyTimeZone });
    const e = DateTime.fromISO(rangeEnd, { zone: companyTimeZone });
    if (s.isValid && e.isValid && s > e) setRangeEnd(rangeStart);
  }, [rangeStart, rangeEnd, companyTimeZone]);

  // ─────────────────────────── fetch expenses
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const snap = await getDocs(
          collection(db, `companies/${companyId}/expenses`)
        );
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setExpenses(rows);
      } catch (e) {
        console.error(e);
        setError("Failed to fetch expenses.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  // ─────────────────────────── fetch employees (ONLY really needed company-wide)
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const snap = await getDocs(
          collection(db, `companies/${companyId}/employees`)
        );
        const map = {};
        snap.forEach((docSnap) => {
          map[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setEmployees(map);
      } catch (e) {
        console.error("Failed to fetch employees for ExpenseAnalytics:", e);
        setEmployees({});
      }
    })();
  }, [companyId]);

  // ─────────────────────────── fetch expense budget
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const ref = doc(
          db,
          `companies/${companyId}/settings`,
          "expense_budget"
        );
        const snap = await getDoc(ref);

        const monthNow = DateTime.now()
          .setZone(companyTimeZone)
          .toFormat("yyyy-LL");
        const yearNow = DateTime.now()
          .setZone(companyTimeZone)
          .toFormat("yyyy");

        if (!snap.exists()) {
          setBudget({
            monthlyBudget: 0,
            yearlyBudget: 0,
            remainingMonthly: 0,
            remainingYearly: 0,
            monthKey: monthNow,
            yearKey: yearNow,
          });
          return;
        }

        const data = snap.data() || {};
        let {
          monthlyBudget = 0,
          yearlyBudget = 0,
          remainingMonthly = 0,
          remainingYearly = 0,
          monthKey = "",
          yearKey = "",
        } = data;

        if (monthKey !== monthNow) {
          remainingMonthly = monthlyBudget;
          monthKey = monthNow;
        }
        if (yearKey !== yearNow) {
          remainingYearly = yearlyBudget;
          yearKey = yearNow;
        }

        setBudget({
          monthlyBudget,
          yearlyBudget,
          remainingMonthly,
          remainingYearly,
          monthKey,
          yearKey,
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId, companyTimeZone]);

  // ─────────────────────────── ✅ scope expenses for drilldown
  const employeeScopedExpenses = useMemo(() => {
    if (!employeeId) return expenses;

    return expenses.filter((e) => {
      const eid = e.employeeId || e.userId || e.uid || null;
      return (eid || "") === employeeId;
    });
  }, [expenses, employeeId]);

  // ✅ Date range filter (TZ-safe)
  const scopedExpenses = useMemo(() => {
    if (!rangeStart || !rangeEnd) return employeeScopedExpenses;

    const start = DateTime.fromISO(rangeStart, {
      zone: companyTimeZone,
    }).startOf("day");
    const end = DateTime.fromISO(rangeEnd, { zone: companyTimeZone }).startOf(
      "day"
    );
    if (!start.isValid || !end.isValid) return employeeScopedExpenses;

    const startMs = start.toMillis();
    const endMs = end.toMillis();

    return employeeScopedExpenses.filter((e) => {
      const iso = getExpenseISODateInTZ(e, companyTimeZone);
      if (!iso) return false;
      const d = DateTime.fromISO(iso, { zone: companyTimeZone }).startOf("day");
      if (!d.isValid) return false;
      const ms = d.toMillis();
      return ms >= startMs && ms <= endMs;
    });
  }, [employeeScopedExpenses, rangeStart, rangeEnd, companyTimeZone]);

  // ─────────────────────────── KPIs (scoped)
  const totalSpend = useMemo(
    () => scopedExpenses.reduce((a, e) => a + Number(e.amount || 0), 0),
    [scopedExpenses]
  );

  const approvedSpend = useMemo(() => {
    return scopedExpenses
      .filter((e) => (e.status || "").toLowerCase() === "approved")
      .reduce((a, e) => a + Number(e.amount || 0), 0);
  }, [scopedExpenses]);

  const pendingSpend = useMemo(() => {
    return scopedExpenses
      .filter((e) => (e.status || "").toLowerCase() === "pending")
      .reduce((a, e) => a + Number(e.amount || 0), 0);
  }, [scopedExpenses]);

  const avgExpense = useMemo(() => {
    return scopedExpenses.length > 0
      ? (totalSpend / scopedExpenses.length).toFixed(2)
      : "0.00";
  }, [scopedExpenses.length, totalSpend]);

  const reimbursableSpend = useMemo(() => {
    return scopedExpenses
      .filter((e) => (e.paymentMethod || "reimbursable") === "reimbursable")
      .reduce((a, e) => a + Number(e.amount || 0), 0);
  }, [scopedExpenses]);

  const cardSpend = useMemo(() => totalSpend - reimbursableSpend, [
    totalSpend,
    reimbursableSpend,
  ]);

  // Budget vs actual (use rangeEnd’s month/year so it matches what user is viewing)
  const monthlyActual = useMemo(() => {
    const endISO =
      rangeEnd ||
      DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();
    const monthKey = DateTime.fromISO(endISO, {
      zone: companyTimeZone,
    }).toFormat("yyyy-LL");

    const monthSpend = scopedExpenses.reduce((sum, e) => {
      const k = toMonthKey(e.expenseDate || e.expenseDateTs || e.date);
      return sum + (k === monthKey ? Number(e.amount || 0) : 0);
    }, 0);

    return Math.max(0, monthSpend);
  }, [scopedExpenses, companyTimeZone, toMonthKey, rangeEnd]);

  const yearlyActual = useMemo(() => {
    const endISO =
      rangeEnd ||
      DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();
    const yearKey = DateTime.fromISO(endISO, {
      zone: companyTimeZone,
    }).toFormat("yyyy");

    const yearSpend = scopedExpenses.reduce((sum, e) => {
      const mk = toMonthKey(e.expenseDate || e.expenseDateTs || e.date); // yyyy-LL
      const y = mk?.split("-")?.[0] || "Unknown";
      return sum + (y === yearKey ? Number(e.amount || 0) : 0);
    }, 0);

    return Math.max(0, yearSpend);
  }, [scopedExpenses, companyTimeZone, toMonthKey, rangeEnd]);

  const monthlyPct = useMemo(() => {
    const b = Number(budget.monthlyBudget || 0);
    if (!b) return null;
    return Math.min(100, (monthlyActual / b) * 100).toFixed(1);
  }, [budget.monthlyBudget, monthlyActual]);

  const yearlyPct = useMemo(() => {
    const b = Number(budget.yearlyBudget || 0);
    if (!b) return null;
    return Math.min(100, (yearlyActual / b) * 100).toFixed(1);
  }, [budget.yearlyBudget, yearlyActual]);

  // ─────────────────────────── Aggregations for charts (scoped)
  const byType = useMemo(() => {
    return scopedExpenses.reduce((acc, e) => {
      const k = e.expenseType || "Other";
      acc[k] = (acc[k] || 0) + Number(e.amount || 0);
      return acc;
    }, {});
  }, [scopedExpenses]);

  const topMerchants = useMemo(() => {
    const byMerchant = scopedExpenses.reduce((acc, e) => {
      const k = (e.merchant || "Unknown").trim() || "Unknown";
      acc[k] = (acc[k] || 0) + Number(e.amount || 0);
      return acc;
    }, {});
    return Object.entries(byMerchant)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [scopedExpenses]);

  // ✅ Monthly trend months based on selected range (cap to last 12 months)
  const months = useMemo(() => {
    if (!rangeStart || !rangeEnd) {
      const arr = [];
      const start = DateTime.now()
        .setZone(companyTimeZone)
        .startOf("month")
        .minus({ months: 11 });
      for (let i = 0; i < 12; i++)
        arr.push(start.plus({ months: i }).toFormat("yyyy-LL"));
      return arr;
    }

    const start = DateTime.fromISO(rangeStart, {
      zone: companyTimeZone,
    }).startOf("month");
    const end = DateTime.fromISO(rangeEnd, { zone: companyTimeZone }).startOf(
      "month"
    );
    if (!start.isValid || !end.isValid) return [];

    const arr = [];
    for (let d = start; d <= end; d = d.plus({ months: 1 })) {
      arr.push(d.toFormat("yyyy-LL"));
      // hard cap safety
      if (arr.length > 60) break;
    }

    // If range spans more than 12 months, show last 12 months ending at rangeEnd
    if (arr.length > 12) return arr.slice(arr.length - 12);
    return arr;
  }, [rangeStart, rangeEnd, companyTimeZone]);

  const monthValues = useMemo(() => {
    const byMonth = scopedExpenses.reduce((acc, e) => {
      const k = toMonthKey(e.expenseDate || e.expenseDateTs || e.date);
      acc[k] = (acc[k] || 0) + Number(e.amount || 0);
      return acc;
    }, {});
    return months.map((m) => byMonth[m] || 0);
  }, [scopedExpenses, months, toMonthKey]);

  const statusCounts = useMemo(() => {
    return scopedExpenses.reduce((acc, e) => {
      const k = (e.status || "Unknown").toLowerCase();
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
  }, [scopedExpenses]);

  // ─────────────────────────── Company-wide ONLY aggregations (still hooks)
  const topEmployees = useMemo(() => {
    if (isDrilldown) return [];
    const byEmployee = scopedExpenses
      .filter((e) => (e.status || "").toLowerCase() === "approved")
      .reduce((acc, e) => {
        const k = e.employeeName || "Unknown";
        acc[k] = (acc[k] || 0) + Number(e.amount || 0);
        return acc;
      }, {});
    return Object.entries(byEmployee)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [isDrilldown, scopedExpenses]);

  const byDepartment = useMemo(() => {
    if (isDrilldown) return {};
    return scopedExpenses.reduce((acc, e) => {
      const emp = employees[e.employeeId] || {};
      const k =
        emp.group || emp.department || emp.departmentName || "Unknown / Unset";
      acc[k] = (acc[k] || 0) + Number(e.amount || 0);
      return acc;
    }, {});
  }, [isDrilldown, scopedExpenses, employees]);

  // ─────────────────────────── Chart options
  const barOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { color: "rgba(0,0,0,0.08)" } },
        y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" } },
      },
    };
  }, []);

  const pieOptions = useMemo(() => {
    return {
      responsive: true,
      cutout: "56%",
      plugins: { legend: { position: "bottom" } },
    };
  }, []);

  const verticalGrad = useCallback((ctx, c1, c2) => {
    const { chartArea, ctx: g } = ctx.chart;
    if (!chartArea) return c1;
    const grad = g.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    grad.addColorStop(0, c1);
    grad.addColorStop(1, c2);
    return grad;
  }, []);

  // ─────────────────────────── Datasets (scoped)
  const typeBarData = useMemo(() => {
    return {
      labels: Object.keys(byType),
      datasets: [
        {
          data: Object.values(byType),
          backgroundColor: (c) =>
            verticalGrad(c, "rgba(59,130,246,0.95)", "rgba(16,185,129,0.55)"),
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
  }, [byType, verticalGrad]);

  const merchantBarData = useMemo(() => {
    return {
      labels: topMerchants.map(([m]) => m),
      datasets: [
        {
          data: topMerchants.map(([, v]) => v),
          backgroundColor: (c) =>
            verticalGrad(c, "rgba(99,102,241,0.95)", "rgba(6,182,212,0.55)"),
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
  }, [topMerchants, verticalGrad]);

  const monthBarData = useMemo(() => {
    return {
      labels: months.map((m) =>
        DateTime.fromFormat(m, "yyyy-LL").toFormat("LLL yyyy")
      ),
      datasets: [
        {
          data: monthValues,
          backgroundColor: (c) =>
            verticalGrad(c, "rgba(34,197,94,0.95)", "rgba(250,204,21,0.55)"),
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, [months, monthValues, verticalGrad]);

  const statusPieData = useMemo(() => {
    return {
      labels: Object.keys(statusCounts).map(
        (s) => s[0].toUpperCase() + s.slice(1)
      ),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: ["#22c55e", "#f59e0b", "#ef4444", "#64748b"],
          borderWidth: 0,
        },
      ],
    };
  }, [statusCounts]);

  const methodPieData = useMemo(() => {
    return {
      labels: ["Reimbursable", "Company card"],
      datasets: [
        {
          data: [reimbursableSpend, cardSpend],
          backgroundColor: ["#3b82f6", "#10b981"],
          borderWidth: 0,
        },
      ],
    };
  }, [reimbursableSpend, cardSpend]);

  // Company-wide datasets (still computed, just not rendered in drilldown)
  const employeeBarData = useMemo(() => {
    return {
      labels: topEmployees.map(([name]) => name),
      datasets: [
        {
          data: topEmployees.map(([, v]) => v),
          backgroundColor: (c) =>
            verticalGrad(c, "rgba(56,189,248,0.95)", "rgba(129,140,248,0.55)"),
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
  }, [topEmployees, verticalGrad]);

  const departmentBarData = useMemo(() => {
    return {
      labels: Object.keys(byDepartment),
      datasets: [
        {
          data: Object.values(byDepartment),
          backgroundColor: (c) =>
            verticalGrad(c, "rgba(244,114,182,0.95)", "rgba(251,191,36,0.55)"),
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
  }, [byDepartment, verticalGrad]);

  // ─────────────────────────── UI (early returns ONLY after hooks)
  if (!companyId) return <p>Loading...</p>;
  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "crimson" }}>{error}</p>;

  return (
    <div className="management-analytics-wrapper">
      {/* ✅ Date range controls */}
      <div className="analytics-rangebar">
        <div className="analytics-filter">
          <label className="analytics-filter-label">Date Range</label>
          <select
            className="analytics-dropdown"
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
          >
            <option value="LAST_7">Last 7 days</option>
            <option value="LAST_30">Last 30 days</option>
            <option value="LAST_90">Last 90 days</option>
            <option value="MTD">Month to date</option>
            <option value="YTD">Year to date</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">Start</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeStart(e.target.value);
            }}
          />
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">End</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeEnd(e.target.value);
            }}
          />
        </div>

        <div className="analytics-range-pill">
          {rangeStart && rangeEnd
            ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
            : "—"}
        </div>
      </div>

      {/* Hint */}
      {isDrilldown ? (
        <div className="analytics-hint">
          Showing Expense analytics for <b>1 employee</b>.
        </div>
      ) : (
        <div className="analytics-hint">
          Showing Expense analytics for <b>all employees</b>.
        </div>
      )}

      {/* KPI cards */}
      <div className="kpi-grid">
        <div className="kpi-card accent-indigo">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Spend</span>
          </div>
          <div className="kpi-value">{money(totalSpend)}</div>
          <div className="kpi-sub">{scopedExpenses.length} expenses</div>
        </div>

        <div className="kpi-card accent-green">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Approved Spend</span>
          </div>
          <div className="kpi-value">{money(approvedSpend)}</div>
          <div className="kpi-sub">Pending: {money(pendingSpend)}</div>
        </div>

        <div className="kpi-card accent-blue">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Avg Expense</span>
          </div>
          <div className="kpi-value">${avgExpense}</div>
          <div className="kpi-sub">Per submission</div>
        </div>

        {/* Budget vs Actual */}
        <div className="kpi-card accent-teal">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">
              Budget vs Actual (Month){isDrilldown ? " · Employee" : ""}
            </span>
          </div>
          <div className="kpi-value">
            {money(monthlyActual)}{" "}
            <span style={{ fontSize: 14, opacity: 0.8 }}>
              / {money(budget.monthlyBudget)}
            </span>
          </div>
          <div className="kpi-sub">
            {monthlyPct != null
              ? `${monthlyPct}% of monthly budget`
              : "No monthly budget set"}
          </div>
        </div>
      </div>

      {yearlyPct != null && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#64748b" }}>
          Year so far: {money(yearlyActual)} of {money(budget.yearlyBudget)} (
          {yearlyPct}%)
        </div>
      )}

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Spend by Category</h3>
          <Bar data={typeBarData} options={barOptions} />
        </div>

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Spend by Payment Method</h3>
          <Pie data={methodPieData} options={pieOptions} />
        </div>

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>
            Monthly Spend{" "}
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              (based on selected range)
            </span>
          </h3>
          <Bar data={monthBarData} options={barOptions} />
        </div>

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Top Merchants</h3>
          <Bar
            data={merchantBarData}
            options={{ ...barOptions, indexAxis: "y" }}
          />
        </div>

        {/* ✅ Company-wide ONLY */}
        {!isDrilldown && (
          <>
            <div className="chart-container" style={{ minHeight: 420 }}>
              <h3>Top 5 Employees by Spend (Approved)</h3>
              <Bar
                data={employeeBarData}
                options={{ ...barOptions, indexAxis: "y" }}
              />
            </div>

            <div className="chart-container" style={{ minHeight: 420 }}>
              <h3>Spend by Department</h3>
              <Bar data={departmentBarData} options={barOptions} />
            </div>
          </>
        )}

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Status Distribution</h3>
          <Pie data={statusPieData} options={pieOptions} />
        </div>
      </div>
    </div>
  );
}
